/**********************************************************
Hawaiian Macadamia Nut Services
Alexander Self
3/30/2020
Tree Inventory Actions
**********************************************************/
import { SET_FORM_TYPE, PREPARE_TREE_INVENTORY, CREATE_INVENTORY_REQUEST, FETCH_INVENTORY,
  ISSUE_OR_RECEIVE_INVENTORY_REQUEST, APPROVE_INVENTORY_REQUEST, DOWNLOAD_INVENTORY,
  SET_INVENTORY_TO_EDIT, EDIT_ISSUE_OR_RECEIVE_INVENTORY_REQUEST, SET_INVENTORY_TO_DELETE,
  DELETE_INVENTORY_REQUEST } from './types';
import { error, success } from './chem_actions';
import { ROOT_URL } from '../../constants';
import { generateID, fillForm } from '../cult_utils';
import { clearForm, fetchFields } from './utils_actions';
import axios from 'axios';
import moment from 'moment';
const FileSaver = require('file-saver');
const $ = window.$;
/**
* fetchInventory(): Fetches all current inventory and the 30 most recent transactions
* @returns {Object} type, payload
* @called-in: ['/components/tree_replant/tree_inventory.js']
**/
export function fetchInventory() {
  $('#preloader').css('display','block');
  return (dispatch) => {
    axios.get(`${ROOT_URL}/fetchInventory`, {
      headers: { authorization: localStorage.getItem('token') }
    }).then((res) => {
      $('#preloader').css('display','none');
      let { inventory, inventoryTransactions, approvers } = res.data;
      dispatch({ type: FETCH_INVENTORY, inventory, inventoryTransactions, approvers });
    }).catch((err) => {
      $('#preloader').css('display','none');
      dispatch(error(`There was an error fetching inventory. ${err}`));
    });
  }
}
/**
* createInventoryRequest(): Creates an inventory request
* @param {Object} data: form data + inventory batch(If new)
* @param {String} formType: type of form and path to server function
* @param {Array} inventory: Master inventory records
* @returns {Object} type, payload
* @called-in: ['/components/tree_replant/tree_inventory.js']
**/
export function createInventoryRequest({ data, formType, inventory }) {
  /* Check whether this is a issuedOrReceived and if an existing inventory location exists
     If inventory does not exist in a location and a user is tryng to issue, throw an error
     to issue from a specific location
     If inventory does exist in a location and a new batch is being created for an existing master record
     throw an error
     Approver required
  */
  let { issuedOrReceived, field, date } = data;
  if (date === '') return error('Please select a date.');
  if (issuedOrReceived === '') return error('Please select an option from the Issue or Receive field');
  if (formType === 'issueOrReceiveInventory' && !inventory.find((f) => f.physicalLocation === field)) {
    return error('Please select a field that contains inventory or create a new batch');
  }
  if (formType === 'createInventoryBatch' && inventory.find((f) => f.physicalLocation === field)) {
    return error('Inventory exists for the field. Select Issue or Receive Inventory to submit transaction.')
  }
  if (formType === 'createInventoryBatch' && issuedOrReceived === 'I') {
    return error('Cannot issue inventory from a batch that does not exist.')
  }
  if (Object.keys(data.approver).length <= 0) return error('Please select an approver from the list.');
  $('#preloader').css('display','block');
  return (dispatch) => {
    axios.post(`${ROOT_URL}/createInventoryRequest`, { data, formType })
      .then((res) => {
        $('#preloader').css('display','none');
        let { inventory, inventoryTransaction, msg, createBatch } = res.data;
        if (!createBatch) return error(msg);
        clearForm('tree-inventory-form');
        document.getElementById('treeInv-issuedOrReceived').selectedIndex = 0;
        success(msg);
        switch (formType) {
          case 'issueOrReceiveInventory':
            dispatch({ type: ISSUE_OR_RECEIVE_INVENTORY_REQUEST, inventoryTransaction });
            break;
          case 'createInventoryBatch':
            dispatch({ type: CREATE_INVENTORY_REQUEST, inventory, inventoryTransaction });
            break;
          default:
            break;
        }
      }).catch((err) => {
        $('#preloader').css('display','none');
        dispatch(error(`There was an error submitting an inventory request. ${err}`));
      });
  }
}
/**
* editInventoryRequest(): Edits an inventory request
* @param {Object} data: form data + inventory batch(If new)
* @param {String} formType: type of form and path to server function
* @param {Array} inventory: Master inventory records
* @returns {Object} type, payload
* @called-in: ['/components/tree_replant/tree_inventory.js->tree_inventory_transaction_table.js']
**/
export function editInventoryRequest({ data, user, oldInventoryRecord, formType, formId, inventory }) {
  let { issuedOrReceived, field, date } = data;
  if (date === '') return error('Please select a date.');
  if (issuedOrReceived === '') return error('Please select an option from the Issue or Receive field');
  if (formType === 'issueOrReceiveInventory' && !inventory.find((f) => f.physicalLocation === field)) {
    return error('Please select a field that contains inventory or create a new batch');
  }
  if (formType === 'createInventoryBatch' && inventory.find((f) => f.physicalLocation === field)) {
    return error('Inventory exists for the field. Select Issue or Receive Inventory to submit transaction.')
  }
  if (formType === 'createInventoryBatch' && issuedOrReceived === 'I') {
    return error('Cannot issue inventory from a batch that does not exist.')
  }
  if (Object.keys(data.approver).length <= 0) return error('Please select an approver from the list.');
  $('#preloader').css('display','block');
  /* Pre-fill missing properties that either need to be updated or referenced */
  data.submitter = user.eid;
  data.editedBy = user.eid;
  data.dateEdited = moment().format('YYYY-MM-DD');
  data.id = oldInventoryRecord.id;
  data.inventoryId = oldInventoryRecord.inventoryId;
  data.masterRecord = 0;
  // This is a master record; if the quantity is different, must update master record
  if (inventory.find((i) => i.inventoryTotal === oldInventoryRecord.quantity && i.physicalLocation === oldInventoryRecord.field &&
    moment(i.lastDateReceived).format('YYYY-MM-DD') === moment(oldInventoryRecord.date).format('YYYY-MM-DD'))) {
    data.masterRecord = 1;
  }
  return (dispatch) => {
    axios.post(`${ROOT_URL}/editInventoryRequest`, { data, formType })
      .then((res) => {
        $('#preloader').css('display','none');
        let { inventoryTransaction, inventoryMaster, msg } = res.data;
        clearForm(formId);
        success(msg);
        
        dispatch({ type: EDIT_ISSUE_OR_RECEIVE_INVENTORY_REQUEST, payload: inventoryTransaction, inventoryMaster });
      }).catch((err) => {
        $('#preloader').css('display','none');
        dispatch(error(`There was an error submitting an inventory request. ${err}`));
      });
  }
}
/**
* deleteInventoryRequest(): Deletes an inventory request
* @param {Number} id: id of inventory
* @returns {Object} type, payload
* @called-in: ['/components/tree_replant/tree_inventory.js->tree_inventory_transaction_table.js']
**/
export function deleteInventoryRequest(inventory) {
  $('#preloader').css('display','block');
  return (dispatch) => {
    axios.post(`${ROOT_URL}/deleteInventoryRequest`, { inventory })
      .then((res) => {
        $('#preloader').css('display','none');
        success(res.data.msg);
        dispatch({ type: DELETE_INVENTORY_REQUEST });
      }).catch((err) => {
        $('#preloader').css('display','none');
        dispatch(error(`There was an error submitting an inventory request. ${err}`));
      });
  }
}
/**
* prepareInventory(): Prepares a tree inventory request
* @param {Object} user: user record
* @param {Object} form: packaged form
* @param {String} formType: type of form
* @returns {Object} type, payload
* @called-in: ['/components/tree_replant/tree_inventory.js']
**/
export function prepareInventory({ user, form, formType }) {
  $('#preloader').css('display','block');
  return (dispatch) => {
    axios.get(`${ROOT_URL}/fetchPreviousInventory`, {
      params: { field: form.field, approver: form.approver },
      headers: { authorization: localStorage.getItem('token') }
    }).then((res) => {
      $('#preloader').css('display','none');
      let { previousInventoryTransactions, approver } = res.data;
      let inventoryId = generateID();
      if (previousInventoryTransactions.length > 0) inventoryId = previousInventoryTransactions[0].inventoryId;
      dispatch({
        type: PREPARE_TREE_INVENTORY,
        payload: {
          ...form,
          submitter: user.eid,
          formType,
          inventoryId,
          approver: approver.length > 0 ? approver[0] : {},
          previousInventoryTransactions
        }
      });
    }).catch((err) => {
      $('#preloader').css('display','none');
      dispatch(error(`There was an error fetching previous inventory. ${err}`));
    });
  }
}
/**
* approveInventoryRequest(): Approves of an inventory transaction
* @param {Object} user: Logged in user
* @param {Object} inventoryTransaction: Inventory request to be approved
* @returns {Object} type, payload
* @called-in: ['/components/tree_replant/tree_inventory.js']
* @notes only users who are approvers and logged in can approve of a inventory request
**/
export function approveInventoryRequest({ user, inventoryTransaction }) {
  $('#preloader').css('display','block');
  return (dispatch) => {
    axios.post(`${ROOT_URL}/approveInventoryRequest`, { user: { eid: user.eid, email: user.email }, inventoryTransaction })
      .then((res) => {
        $('#preloader').css('display','none');
        let { msg, inventoryTransaction, inventoryMaster } = res.data;
        success(msg);
        console.log(res.data)
        dispatch({ type: APPROVE_INVENTORY_REQUEST, inventoryTransaction, inventoryMaster });
      }).catch((err) => {
        $('#preloader').css('display','none');
        dispatch(error(`There was an error approving an inventory request. ${err}`));
      });
  }
}
/**
* downloadInventory(): Downloads inventory record(s)
* @param {Object} i: inventory master object
* @returns {Object} type, payload
* @called-in: ['/components/tree_replant/tree_inventory.js']
**/
export function downloadInventory(i) {
  $('#preloader').css('display','block');
  return (dispatch) => {
    axios.get(`${ROOT_URL}/downloadInventory`, {
      params: { inventoryId: i.inventoryId },
      headers: { authorization: localStorage.getItem('token') }
    }).then((res) => {
      $('#preloader').css('display','none');
       let { msg, csv } = res.data; 
      success(msg);
      var blob = new Blob([csv], {type: "text/plain;charset=utf-8"});
      FileSaver.saveAs(blob, `Inventory Transactions - ${i.physicalLocation} - ${moment().format('YYYY-MM-DD')}.csv`);
      dispatch({ type: DOWNLOAD_INVENTORY });
    }).catch((err) => {
      $('#preloader').css('display','none');
      dispatch(error(`There was an error fetching inventory. ${err}`));
    });
  }
}
/**
* setFormType(): Sets the type of form: Creating a new inventory batch; or issuing or receiving
  inventory
* @param {String} formType: Create New Inventory Batch or Issue or Receive Inventory
* @returns {Object} type, payload
* @called-in: ['/components/tree_replant/tree_inventory.js']
**/
export function setFormType(formType) {
  if (formType === 'createInventoryBatch') {
    formType = 'issueOrReceiveInventory';
    document.getElementById('treeInv-issuedOrReceived').selectedIndex = 0;
    document.getElementById('treeInv-issuedOrReceived').value = 'I';
  } else if (formType === 'issueOrReceiveInventory') {
    formType = 'createInventoryBatch';
    document.getElementById('treeInv-issuedOrReceived').selectedIndex = 1;
    document.getElementById('treeInv-issuedOrReceived').value = 'R';
  }
  return {
    type: SET_FORM_TYPE,
    payload: formType
  }
}
/**
* setInventoryToEdit(): Sets the inventory to be edited
* @param {Object} inventory: copy of inventory to be edited
* @param {String} formId: Id of form to be pre-filled
* @returns {Object} type, payload
* @called-in: ['/components/tree_replant/tree_inventory.js->tree_inventory_transaction_table.js']
**/
export function setInventoryToEdit({inventory, formId}) {
  /* 
     Fill form
     Set  
  */
 return (dispatch) => {
  dispatch(fetchFields(inventory.location));
  setTimeout(() => {
    fillForm(formId, inventory);
  }, 200);
  dispatch({ type: SET_INVENTORY_TO_EDIT, payload: inventory}); 
 }
}
/**
* setInventoryToDelete(): Sets the inventory to be deleted
* @param {Object} inventory: copy of inventory to be edited
* @returns {Object} type, payload
* @called-in: ['/components/tree_replant/tree_inventory.js->tree_inventory_transaction_table.js']
**/
export function setInventoryToDelete(inventory) {
  return {
    type: SET_INVENTORY_TO_DELETE,
    payload: inventory
  }
}





































/* END */



// WEBPACK FOOTER //
// ./src/cultivation/actions/tree_inventory_actions.js